"use client";

/**
 * SettingsMenu — unified dropdown menu for header actions.
 *
 * Consolidates what was previously three separate header buttons
 * (LocaleSwitcher, admin gear, and the push/install settings) into a
 * single compact gear-icon dropdown. The menu contains:
 *
 * 1. **Language** — a sub-menu with the supported locales
 * 2. **Notifications** — a toggle to enable/disable Web Push
 * 3. **Install app** — triggers the native install prompt (or shows
 *    iOS instructions via a tooltip)
 * 4. **Administrative entries** — tenant administration for platform admins,
 *    plus shared-agent configuration for platform and tenant admins
 *
 * The user's display name is shown at the bottom.
 */

import { useCallback, useEffect, useState } from "react";
import { purgeAllUiCache } from "@/lib/ui-cache";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-browser-location";
import {
  Settings,
  Globe,
  Check,
  Bell,
  BellOff,
  Download,
  Shield,
  ChevronRight,
  Share,
  LogOut,
  Link2,
  SlidersHorizontal,
  Receipt,
} from "lucide-react";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  setLocale,
  getCurrentLocale,
  SUPPORTED_LOCALES,
  LOCALE_LABELS,
  type Locale,
} from "@/lib/i18n";
import { useInstallPrompt } from "@/hooks/use-install-prompt";
import type { PushSubscriptionAPI } from "@/hooks/use-push-subscription";
import type { Agent } from "@/hooks/use-agents";
import type { UseUserPreferencesReturn } from "@/hooks/use-user-preferences";
import { UserPreferencesDialog } from "@/components/user-preferences-dialog";
import { OPEN_PREFERENCES_EVENT } from "@/components/enter-to-send-hint";

export interface SettingsMenuProps {
  className?: string;
  user: { firstName?: string | null; email?: string | null };
  /** Full ae10 platform-admin capability. */
  isAdminUser: boolean;
  /** Narrow shared-agent editor capability for a tenant-admin. */
  canManageTenantAgents?: boolean;
  pushApi: PushSubscriptionAPI;
  preferencesApi: UseUserPreferencesReturn;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
}

export function SettingsMenu({
  className,
  user,
  isAdminUser,
  canManageTenantAgents = false,
  pushApi,
  preferencesApi,
  agents,
  agentsLoading,
  agentsError,
}: SettingsMenuProps) {
  const { t } = useTranslation();
  const currentLocale = getCurrentLocale();
  const installPrompt = useInstallPrompt();
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const openPreferences = () => setPreferencesOpen(true);
    window.addEventListener(OPEN_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, openPreferences);
  }, []);

  const handleSelectLocale = useCallback((locale: Locale) => {
    setLocale(locale);
  }, []);

  const handleTogglePush = useCallback(() => {
    if (pushApi.pushEnabled) {
      void pushApi.disable();
    } else {
      void pushApi.enable();
    }
  }, [pushApi]);

  const handleInstall = useCallback(async () => {
    if (installPrompt.isIOS) {
      setShowIOSHint((v) => !v);
      return;
    }
    if (installPrompt.canInstall) {
      await installPrompt.promptInstall();
    }
  }, [installPrompt]);

  const canShowInstall =
    !installPrompt.isInstalled && (installPrompt.canInstall || installPrompt.isIOS);

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t("common:header.settings", { defaultValue: "Settings" })}
          >
            <Settings className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* ── Language sub-menu ── */}
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {t("common:header.settings", { defaultValue: "Settings" })}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-xs">
              <Globe className="size-4 mr-1" />
              {t("common:header.language", { defaultValue: "Language" })}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-40">
              {SUPPORTED_LOCALES.map((locale) => (
                <DropdownMenuItem
                  key={locale}
                  className={cn(
                    "flex items-center justify-between text-xs",
                    locale === currentLocale && "bg-accent",
                  )}
                  onClick={() => handleSelectLocale(locale)}
                >
                  <span>{LOCALE_LABELS[locale]}</span>
                  {locale === currentLocale && (
                    <Check className="size-3 text-muted-foreground" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* ── Push notifications toggle ── */}
          {pushApi.isSupported && (
            <DropdownMenuItem
              className="text-xs"
              onClick={handleTogglePush}
              disabled={pushApi.enabling}
            >
              {pushApi.pushEnabled ? (
                <Bell className="size-4 mr-1 text-green-500" />
              ) : (
                <BellOff className="size-4 mr-1" />
              )}
              {pushApi.pushEnabled
                ? t("common:header.notificationsOn", { defaultValue: "Notifications on" })
                : t("common:header.enableNotifications", { defaultValue: "Enable notifications" })}
            </DropdownMenuItem>
          )}

          {/* ── Push permission denied hint ── */}
          {pushApi.isSupported &&
            !pushApi.pushEnabled &&
            pushApi.permission === "denied" && (
              <p className="px-2 py-1 text-[10px] text-muted-foreground">
                {t("common:header.notificationsBlocked", {
                  defaultValue: "Blocked — enable in browser settings",
                })}
              </p>
            )}

          {/* ── Connections ── */}
          <DropdownMenuItem
            className="text-xs"
            onClick={() => {
              navigate("/connections");
            }}
          >
            <Link2 className="size-4 mr-1" />
            <span className="flex-1">{t("connections:menuItem")}</span>
            <ChevronRight className="ml-auto size-3" />
          </DropdownMenuItem>

          {/* ── User preferences ── */}
          <DropdownMenuItem
            className="text-xs"
            onClick={() => setPreferencesOpen(true)}
          >
            <SlidersHorizontal className="size-4 mr-1" />
            <span className="flex-1">{t("preferences.title")}</span>
          </DropdownMenuItem>

          {/* ── Usage ── */}
          <DropdownMenuItem
            className="text-xs"
            onClick={() => {
              navigate("/usage");
            }}
          >
            <Receipt className="size-4 mr-1" />
            <span className="flex-1">{t("header.usage")}</span>
            <ChevronRight className="ml-auto size-3" />
          </DropdownMenuItem>

          {/* ── Install app ── */}
          {canShowInstall && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-xs" onClick={handleInstall}>
                <Download className="size-4 mr-1" />
                {t("common:header.installApp", { defaultValue: "Install app" })}
              </DropdownMenuItem>
              {showIOSHint && installPrompt.isIOS && (
                <p className="px-2 py-1 text-[10px] text-muted-foreground">
                  {t("common:header.iosInstallHint", {
                    defaultValue:
                      "Tap Share → Add to Home Screen",
                  })}
                </p>
              )}
            </>
          )}

          {/* ── Administration ── */}
          {(isAdminUser || canManageTenantAgents) && (
            <>
              <DropdownMenuSeparator />
              {isAdminUser && (
                <DropdownMenuItem
                  className="text-xs"
                  onClick={() => navigate("/admin/tenants")}
                >
                  <Shield className="size-4 mr-1" />
                  {t("header.adminPanel", { defaultValue: "Tenant administration" })}
                  <ChevronRight className="ml-auto size-3" />
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-xs"
                onClick={() => navigate("/admin/agent-config")}
              >
                <Shield className="size-4 mr-1" />
                {t("header.agentConfiguration", { defaultValue: "Agent configuration" })}
                <ChevronRight className="ml-auto size-3" />
              </DropdownMenuItem>
            </>
          )}

          {/* ── User display name ── */}
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5">
            <p className="text-xs text-muted-foreground truncate">
              {user.firstName ?? user.email}
            </p>
          </div>

          {/* ── Sign out ── */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-xs"
            onClick={async () => {
              await purgeAllUiCache();
              window.location.href = `${window.location.pathname}?logout=1`;
            }}
          >
            <LogOut className="size-4 mr-1" />
            {t("common:header.signOut", { defaultValue: "Sign out" })}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <UserPreferencesDialog
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
        preferencesApi={preferencesApi}
        agents={agents}
        agentsLoading={agentsLoading}
        agentsError={agentsError}
        isAdminUser={isAdminUser}
      />
    </div>
  );
}
